import { Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';

/**
 * Props for `Timeline`.
 */
export type TimelineProps = SliceComponentProps<Content.TimelineSlice>;

/**
 * Component for "Timeline" Slices.
 */
const Timeline = ({ slice }: TimelineProps): JSX.Element => {
  const {
    small_title: smallTitle,
    title,
    image,
    steps,
    button_label: buttonLabel,
    button_link: buttonLink,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-mint-green-gradient-1 px-5 md:px-16 py-20"
    >
      <div className="max-w-screen-2xl mx-auto flex flex-col items-center gap-10">
        <div className="text-center flex flex-col gap-4 max-w-[33rem] mx-auto">
          <span className="text-regular-semibold">
            <PrismicRichText field={smallTitle} />
          </span>
          <PrismicRichText field={title} />
        </div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-10">
          <div className="md:w-1/2">
            <PrismicNextImage
              field={image}
              className="object-cover h-[488px] w-[488px] rounded-[20px]"
            />
          </div>

          <div className="md:1/2">
            {steps.map((step, index) => (
              <div
                key={index}
                className="group flex w-full hover:bg-hover-mint-green-gradient pl-10"
              >
                <div className="z-10 flex w-full gap-x-5">
                  <div className="z-10 mt-6 flex-shrink-0">
                    <PrismicNextImage field={step.icon} />
                  </div>
                  <div className="flex flex-col gap-y-4 p-4">
                    <PrismicRichText field={step.title} />
                    <PrismicRichText
                      field={step.description}
                      components={{
                        paragraph: ({ children }) => (
                          <p className="text-large-normal">{children}</p>
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <PrismicNextLink
          field={buttonLink}
          className="button-bordered-shade-green text-black"
        >
          <span>{buttonLabel}</span>
        </PrismicNextLink>
      </div>
    </section>
  );
};

export default Timeline;
